import { theme, useResponsive } from "@product/scmp-sdk";
import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";
import type { FragmentRefs } from "relay-runtime";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { superSplashLargeLiveWidget$key } from "~/queries/__generated__/superSplashLargeLiveWidget.graphql";

import type { LabelType } from "./styles";
import {
  Container,
  Contents,
  Description,
  Label,
  LabelContainer,
  RestArticlesContainer,
  StyledPrimaryContentItemRender,
  StyledSecondaryContentItemRender,
  TwoArticlesInDesktopContainer,
  TwoArticlesInMobileContainer,
} from "./styles";

type Props = {
  className?: string;
  labelType: LabelType;
  reference: superSplashLargeLiveWidget$key;
};

export const HomeSuperSplashLarge: FunctionComponent<Props> = ({
  className,
  labelType,
  reference: reference_,
}) => {
  const data = useFragment(
    graphql`
      fragment superSplashLargeLiveWidget on LiveWidget {
        label
        description
        relatedArticles {
          ...contentItemRenderContent
        }
      }
    `,
    reference_,
  );

  const label = data?.label;
  const description = data?.description;
  const [firstArticle, secondArticle, thirdArticle, ...restArticles] = data?.relatedArticles ?? [];
  const superSplashTwoArticles = [secondArticle, thirdArticle];

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const renderContent = (
    articles: Nullish<{
      readonly " $fragmentSpreads": FragmentRefs<"contentItemRenderContent">;
    }>[],
  ) =>
    articles.map(
      (article, key) =>
        article && (
          <StyledSecondaryContentItemRender
            key={key}
            reference={article}
            variant="home-super-splash-live-secondary"
            withComment={isMobile}
            onClick={entityId => {
              sendGATracking({
                action: "Click/top_story_breaking",
                category: "Homepage Depth",
                label: entityId,
              });
            }}
          />
        ),
    );

  return (
    <Container className={className}>
      <LabelContainer $labelType={labelType}>
        <Label $labelType={labelType}>{label}</Label>
        {description && <Description $labelType={labelType}>{description}</Description>}
      </LabelContainer>
      <Contents>
        {firstArticle && (
          <StyledPrimaryContentItemRender
            reference={firstArticle}
            variant="home-super-splash-large-primary"
            onClick={entityId => {
              sendGATracking({
                action: "Click/top_story_breaking",
                category: "Homepage Depth",
                label: entityId,
              });
            }}
          >
            <TwoArticlesInDesktopContainer>
              {renderContent(superSplashTwoArticles)}
            </TwoArticlesInDesktopContainer>
          </StyledPrimaryContentItemRender>
        )}
        <TwoArticlesInMobileContainer>
          {renderContent(superSplashTwoArticles)}
        </TwoArticlesInMobileContainer>
        {restArticles?.length > 0 && (
          <RestArticlesContainer>{renderContent(restArticles)}</RestArticlesContainer>
        )}
      </Contents>
    </Container>
  );
};

HomeSuperSplashLarge.displayName = "HomeSuperSplashLarge";
